import type { GridDataSource } from '@mui/x-data-grid-pro';
import { nanoid } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo } from 'react';
import { useDataColumns } from '@/shared/hooks';
import { Analytics } from '@/shared/lib';
import { useDataFetchersContext } from '../DataFetchersContext';

interface UseDataParams {
  dimensions: string[];
}

export const useData = ({
  dimensions,
}: UseDataParams): {
  dataSource: GridDataSource;
  dataColumns: string[];
} => {
  const { onGetTreeData, onGetTotalData } = useDataFetchersContext();
  const dataColumnsComparator = useCallback((a: string, b: string) => {
    return a.localeCompare(b, undefined, { numeric: true });
  }, []);
  const { dataColumns, addColumns, reset } = useDataColumns(
    dataColumnsComparator,
  );
  const dataSource: GridDataSource = useMemo(() => {
    return {
      getRows: async (params) => {
        Analytics.sendFeatureUsage(
          'tree_data_grid',
          params.groupKeys && params.groupKeys.length > 0
            ? 'fetch_group_rows'
            : 'fetch_rows',
        );
        const dataResopnses = await Promise.all([
          params.groupKeys?.length === 0
            ? onGetTotalData().then((total) => {
                addColumns(Object.keys(total));

                return {
                  ...total,
                  path: ['total'],
                  isTotal: true,
                };
              })
            : Promise.resolve([]),
          onGetTreeData(
            params.groupKeys?.map((groupKey, index) => {
              return {
                dimension: dimensions[index],
                value: groupKey,
              };
            }) || [],
          ).then(({ rows, columns }) => {
            addColumns(columns);

            return rows.map((row, ind) => {
              return {
                ...row,
                firstInGroup: ind === 0,
                lastInGroup: ind === rows.length - 1,
              };
            });
          }),
        ]);

        return {
          rows: dataResopnses.flat().map((row) => {
            return {
              id: nanoid(),
              ...row,
            };
          }),
        };
      },
      getGroupKey: (row) => {
        if (row.isTotal) {
          return row.path.at(-1);
        }

        const dimension = dimensions[row.path.length - 1];

        return row[dimension];
      },
      getChildrenCount: (row) => {
        if (row.isTotal || row.path.length >= dimensions.length) {
          return 0;
        }

        return 1;
      },
    };
  }, [onGetTreeData, onGetTotalData, addColumns, dimensions]);

  useEffect(() => {
    reset();
  }, [onGetTreeData, onGetTotalData, reset]);

  return {
    dataSource,
    dataColumns,
  };
};
